import { useEffect, useState } from 'react';
import {
  Table, Group, Stack, Text, Button, SegmentedControl, Alert, Badge, ActionIcon, Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle, IconRefresh, IconDeviceFloppy, IconWand, IconTrash, IconCertificate, IconLock,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SectionCard } from '../SectionCard';
import { EmptyState } from '../EmptyState';
import { TableSkeleton } from '../TableSkeleton';
import { confirmDelete } from '../../lib/confirm';
import { SILENT_ERROR } from '../../lib/queryMeta';
import {
  apiError, apiStatus, fmtAvg, gradeColor, normalizeTermGradeRows, type TermGradeRow,
} from '../../lib/journal';

interface Props {
  classId: string | null;
  subjectId: string | null;
  termId: string | null;
}

const VALUES = ['1', '2', '3', '4', '5'];

/**
 * Chorak yakuni (§2.3 + §6.5).
 *
 * QULFLANGAN QAROR: chorak bahosini O'QITUVCHI qo'yadi — tizim faqat joriy
 * baholardan o'rtachani hisoblab TAKLIF qiladi. Shuning uchun taklif va yakuniy
 * baho ALOHIDA ustunlarda turadi va farq qilgani vizual belgilanadi
 * (audit: "tizim 4 taklif qildi, o'qituvchi 5 qo'ydi").
 *
 * 403 (§6.5): TEACHER chorak bahosini FAQAT `TeacherAssignment` bor fandan qo'ya oladi —
 * sinf rahbari bo'lish yetarli EMAS. Yangi maktabda biriktirishlar to'ldirilmagunча bu
 * ekran 403 beradi, shu sabab xom xato o'rniga nima qilish kerakligi aytiladi.
 */
export function TermSummary({ classId, subjectId, termId }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});

  // §6.5: uchala parametr ham MAJBURIY (aks holda backend 400 qaytaradi)
  const ready = !!classId && !!subjectId && !!termId;

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['term-grades', classId, subjectId, termId],
    queryFn: async () => {
      const res = await api.get('/term-grades', { params: { classId, subjectId, termId } });
      return normalizeTermGradeRows(res.data);
    },
    enabled: ready,
    meta: SILENT_ERROR,
  });

  // Serverdagi holat — tahrirlash uchun boshlang'ich qiymat. Saqlangandan keyin
  // ro'yxat qayta so'raladi va bu yerda yana serverdagi haqiqat bilan tenglashadi.
  useEffect(() => {
    if (!data) return;
    const init: Record<string, string> = {};
    data.forEach((r) => { if (r.termGrade?.value != null) init[r.studentId] = String(r.termGrade.value); });
    setValues(init);
  }, [data]);

  const rows: TermGradeRow[] = data ?? [];
  const filled = Object.values(values).filter(Boolean).length;
  /** 403 — o'qituvchi bu fanga biriktirilmagan; boshqa xatolardan alohida ko'rsatiladi. */
  const forbidden = isError && apiStatus(error) === 403;

  /** Taklifi bor barcha o'quvchilarga taklifni qo'yadi (keyin qo'lda tuzatish mumkin). */
  const applySuggestions = () => {
    const next: Record<string, string> = { ...values };
    rows.forEach((r) => { if (r.suggested != null) next[r.studentId] = String(r.suggested); });
    setValues(next);
  };

  const notifyError = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: apiStatus(e) === 403 ? t('termSummary.notAssigned') : apiError(e, t('common.error')),
    });

  const saveMut = useMutation({
    mutationFn: () => {
      const items = Object.entries(values)
        .filter(([, v]) => !!v)
        .map(([studentId, v]) => ({ studentId, value: Number(v) }));
      return api.post('/term-grades/bulk', { subjectId, termId, items });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('termSummary.saved') });
      qc.invalidateQueries({ queryKey: ['term-grades'] });
      qc.invalidateQueries({ queryKey: ['grades-journal'] });
      qc.invalidateQueries({ queryKey: ['grades-report'] });
    },
    onError: notifyError,
  });

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/term-grades/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('termSummary.cancelled') });
      qc.invalidateQueries({ queryKey: ['term-grades'] });
      qc.invalidateQueries({ queryKey: ['grades-journal'] });
      qc.invalidateQueries({ queryKey: ['grades-report'] });
    },
    onError: notifyError,
  });

  const onSave = () => {
    // Takroriy yuborishdan himoya: `POST /term-grades/bulk` upsert bo'lsa ham,
    // ikki marta bosish keraksiz so'rov va chalkash toast beradi.
    if (saveMut.isPending) return;
    if (!filled) {
      notifications.show({ color: 'orange', message: t('termSummary.nothingToSave') });
      return;
    }
    saveMut.mutate();
  };

  return (
    <SectionCard
      title={t('termSummary.title')}
      icon={IconCertificate}
      subtitle={t('termSummary.desc')}
      p={0}
      right={
        <Group gap="xs" wrap="nowrap">
          <Button size="compact-sm" variant="light" leftSection={<IconWand size={15} />} disabled={!rows.length} onClick={applySuggestions}>
            {t('termSummary.confirmAll')}
          </Button>
          <Button size="compact-sm" leftSection={<IconDeviceFloppy size={15} />} disabled={!ready || !filled} loading={saveMut.isPending} onClick={onSave}>
            {t('termSummary.confirm')}
          </Button>
        </Group>
      }
    >
      {!ready ? (
        <EmptyState icon={IconCertificate} title={t('journal.selectFilters')} description={t('termSummary.needTerm')} />
      ) : isLoading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : forbidden ? (
        // Xom 403 emas — nima qilish kerakligi aytiladi (biriktirish `/schedule` da)
        <Stack align="center" gap="sm" p="lg">
          <Alert color="orange" variant="light" icon={<IconLock size={18} />} title={t('termSummary.notAssigned')} w="100%">
            {t('termSummary.notAssignedDesc')}
          </Alert>
        </Stack>
      ) : isError ? (
        <Stack align="center" gap="sm" p="lg">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
            {t('termSummary.loadError')}
          </Alert>
          <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
            {t('terms.retry')}
          </Button>
        </Stack>
      ) : !rows.length ? (
        <EmptyState icon={IconCertificate} title={t('termSummary.empty')} description={t('termSummary.emptyDesc')} />
      ) : (
        <Table.ScrollContainer minWidth={960}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('journal.student')}</Table.Th>
                <Table.Th w={110} ta="center">{t('termSummary.gradeCount')}</Table.Th>
                <Table.Th w={100} ta="center">{t('journal.avg')}</Table.Th>
                <Table.Th w={110} ta="center">{t('termSummary.suggested')}</Table.Th>
                <Table.Th w={250}>{t('termSummary.finalGrade')}</Table.Th>
                <Table.Th w={210}>{t('fields.status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const saved = r.termGrade;
                const current = values[r.studentId] ?? '';
                // Tanlangan qiymat HOZIRGI taklifdan farq qiladimi
                const differs = !!current && r.suggested != null && Number(current) !== r.suggested;
                // §6.5 audit: yozilgan paytdagi taklif hozirgisidan farq qiladimi
                const staleSuggestion =
                  saved?.suggested != null && r.suggested != null && saved.suggested !== r.suggested;
                return (
                  <Table.Tr key={r.studentId}>
                    <Table.Td fw={500}>{r.fullName}</Table.Td>
                    <Table.Td ta="center">
                      <Badge variant="light" color={r.gradeCount ? 'blue' : 'gray'}>{r.gradeCount ?? 0}</Badge>
                    </Table.Td>
                    <Table.Td ta="center" fw={600}>{fmtAvg(r.avg)}</Table.Td>
                    <Table.Td ta="center">
                      {r.suggested != null ? (
                        <Badge size="lg" variant="light" color={gradeColor(r.suggested)}>{r.suggested}</Badge>
                      ) : (
                        <Tooltip label={t('termSummary.noSuggestion')}>
                          <Text c="dimmed" fz="sm" lh={1.4}>—</Text>
                        </Tooltip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <SegmentedControl
                        size="xs"
                        data={VALUES}
                        value={current}
                        onChange={(v) => {
                          // Qiymat updater ICHIDA emas — tashqarida olingan `v`.
                          setValues((prev) => ({ ...prev, [r.studentId]: v }));
                        }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        {saved ? (
                          <Badge color="teal" variant="light">{t('termSummary.alreadySet')}</Badge>
                        ) : (
                          <Badge color="gray" variant="light">{t('termSummary.pending')}</Badge>
                        )}
                        {differs && (
                          <Tooltip label={t('termSummary.changedHint', { n: r.suggested })}>
                            <Badge color="orange" variant="filled">{t('termSummary.changed')}</Badge>
                          </Tooltip>
                        )}
                        {staleSuggestion && (
                          <Tooltip label={t('termSummary.staleHint', { old: saved?.suggested, now: r.suggested })}>
                            <Badge color="grape" variant="light">{t('termSummary.stale')}</Badge>
                          </Tooltip>
                        )}
                        {saved?.id && (
                          <Tooltip label={t('termSummary.cancelGrade')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('termSummary.cancelGrade')}
                              loading={delMut.isPending && delMut.variables === saved.id}
                              onClick={() => confirmDelete(r.fullName, () => delMut.mutate(saved.id!))}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </SectionCard>
  );
}
